import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import '../CallForm/CallForm.css'; // Use same styles as existing forms

const DynamicLeadForm = () => {
  const { formSlug } = useParams();
  const navigate = useNavigate();
  
  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    loadFormConfig();
  }, [formSlug]);

  const loadFormConfig = async () => {
    try {
      setIsLoading(true);
      
      // Fetch form configuration by slug
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/forms/by-slug/${formSlug}`);
      
      if (response.data.status === 'success') {
        const config = response.data.form;
        setFormConfig(config);
        
        // Initialize form data with empty values based on field configuration
        const initialData = {
          Timezone: 'ET' // Default timezone
        };
        
        config.fields.forEach(field => {
          initialData[field.name] = field.default_value || '';
        });
        
        setFormData(initialData);
      }
    } catch (err) {
      console.error('Error loading form:', err);
      setFormConfig({ error: 'Form not found' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Remove @ symbols from social media usernames
    let processedValue = value;
    if (['IG_Username', 'TT_Username', 'YT_Username', 'Snap_Username'].includes(name)) {
      processedValue = value.replace(/@/g, '');
    }
    
    // Handle DOB formatting
    if (name === 'DOB') {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 8);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Generate client_id when we have all required fields
    if ((name === 'First_Legal_Name' || name === 'Last_Legal_Name' || name === 'DOB') &&
        formData.First_Legal_Name && 
        formData.Last_Legal_Name && 
        (name === 'DOB' ? processedValue : formData.DOB)) {
      const firstInitial = (name === 'First_Legal_Name' ? processedValue : formData.First_Legal_Name).charAt(0).toLowerCase();
      const lastInitial = (name === 'Last_Legal_Name' ? processedValue : formData.Last_Legal_Name).charAt(0).toLowerCase();
      const dob = name === 'DOB' ? processedValue : formData.DOB;
      const clientId = `${firstInitial}${lastInitial}${dob}`;
      
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        client_id: clientId
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    formConfig.fields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      }
      
      // Email validation
      if (field.name === 'Email_Address' && formData[field.name]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.name])) {
          newErrors[field.name] = 'Please enter a valid email address';
        }
      }
      
      // DOB validation
      if (field.name === 'DOB' && formData[field.name]) {
        if (formData[field.name].length !== 8) {
          newErrors[field.name] = 'Date of birth must be in MMDDYYYY format';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare submission data matching CallFormData schema
      const enrichedFormData = {
        ...formData,
        source: formConfig.source_id,
        collection: 'ClientInfo',
        // Set default values for metrics (matching the schema)
        IG_Followers: formData.IG_Followers || 0,
        IG_Engagement: formData.IG_Engagement || 0,
        IG_Rank: formData.IG_Rank || 0,
        IG_Views_Rank: formData.IG_Views_Rank || 0,
        IG_Verified: formData.IG_Verified || false,
        TT_Followers: formData.TT_Followers || 0,
        TT_Rank: formData.TT_Rank || 0,
        TT_Views_Rank: formData.TT_Views_Rank || 0,
        TT_Engagement: formData.TT_Engagement || 0,
        TT_Verified: formData.TT_Verified || false,
        YT_Followers: formData.YT_Followers || 0,
        YT_Rank: formData.YT_Rank || 0,
        YT_Views_Rank: formData.YT_Views_Rank || 0,
        YT_Engagement: formData.YT_Engagement || 0,
        YT_Verified: formData.YT_Verified || false,
        Snap_Followers: formData.Snap_Followers || "0",
        Snap_Star: formData.Snap_Star || false,
        Snap_Monetized: formData.Snap_Monetized || false
      };
      
      // Submit to /call endpoint (same as existing forms)
      const response = await axios.post(
        `${API_ENDPOINTS.API_BASE_URL}/call`,
        enrichedFormData
      );
      
      if (response.data.status === 'success') {
        // Show thank you page
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const fieldConfig = {
      className: `snapped-call-form__input ${errors[field.name] ? 'snapped-call-form__input--error' : ''}`,
      name: field.name,
      value: formData[field.name] || '',
      onChange: handleChange,
      required: field.required,
      placeholder: field.placeholder || field.label
    };

    switch (field.type) {
      case 'email':
        return (
          <input
            {...fieldConfig}
            type="email"
          />
        );
      
      case 'tel':
        return (
          <input
            {...fieldConfig}
            type="tel"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            {...fieldConfig}
            rows={field.rows || 4}
          />
        );
      
      case 'select':
        return (
          <select {...fieldConfig}>
            <option value="">{field.placeholder || `Please select...`}</option>
            {field.options?.map(option => {
              // Handle both string and object formats
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        );
      
      case 'dob':
        return (
          <input
            {...fieldConfig}
            type="text"
            maxLength={8}
            placeholder="Date of Birth (MMDDYYYY) *"
          />
        );
      
      default:
        return (
          <input
            {...fieldConfig}
            type="text"
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  if (!formConfig || formConfig.error) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <h1 className="snapped-call-form__title">Form Not Found</h1>
          <p>The form you're looking for doesn't exist or isn't active.</p>
          <button onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  // Show thank you page after successful submission
  if (isSubmitted) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h1 className="snapped-call-form__title" style={{ color: '#10b981', marginBottom: '16px' }}>
            Thank You!
          </h1>
          <p style={{ fontSize: '18px', color: '#374151', lineHeight: '1.6' }}>
            Look out for an email with more information!
          </p>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '24px' }}>
            We've received your submission and will be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  // Group fields by section
  const sections = {};
  formConfig.fields.forEach(field => {
    const section = field.section || 'General';
    if (!sections[section]) {
      sections[section] = [];
    }
    sections[section].push(field);
  });

  return (
    <div className="call-form-page call-form">
      <div className="snapped-call-form__container">
        <h1 className="snapped-call-form__title">{formConfig.name}</h1>
        {formConfig.description && (
          <p className="snapped-call-form__helper-text">{formConfig.description}</p>
        )}
        
        <form onSubmit={handleSubmit}>
          {Object.entries(sections).map(([sectionName, fields]) => (
            <div key={sectionName} className="snapped-call-form__section">
              <h3 className="snapped-call-form__section-heading">{sectionName}</h3>
              
              {fields.map(field => (
                <div key={field.name}>
                  {field.type === 'select' ? (
                    <div className="snapped-call-form__input-group">
                      {field.label && field.label !== field.placeholder && (
                        <label htmlFor={field.name} className="snapped-call-form__label">
                          {field.label}
                        </label>
                      )}
                      {renderField(field)}
                      {errors[field.name] && (
                        <span className="snapped-call-form__error-message">{errors[field.name]}</span>
                      )}
                    </div>
                  ) : (
                    <>
                      {renderField(field)}
                      {errors[field.name] && (
                        <span className="snapped-call-form__error-message">{errors[field.name]}</span>
                      )}
                    </>
                  )}
                  
                  {field.helper_text && (
                    <p className="snapped-call-form__helper-text">{field.helper_text}</p>
                  )}
                </div>
              ))}
            </div>
          ))}
          
          {errors.submit && (
            <div className="snapped-call-form__error-message">{errors.submit}</div>
          )}
          
          <button 
            type="submit" 
            className="snapped-call-form__submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : formConfig.submit_button_text || 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DynamicLeadForm;